import { useState } from 'react';
import { Drawer, Form, Input, Select, Button, Space, Typography, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api/client';
import { useDrawerWidth } from '../hooks/useIsMobile';

interface Props {
  open: boolean;
  roles: { value: string; label: string }[];
  onClose: () => void;
}

/** 邀請使用者：可寄信或產生邀請連結。後台面板一律用 Drawer。 */
export default function InviteUserDrawer({ open, roles, onClose }: Props) {
  const drawerWidth = useDrawerWidth(480);
  const [form] = Form.useForm<{ email: string; roleId: string }>();
  const [link, setLink] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: (v: { email: string; roleId: string; send: boolean }) => api.invite(v.email, v.roleId, v.send),
    onSuccess: (d, v) => {
      setLink(d.acceptUrl);
      if (!v.send) message.success('已產生邀請連結');
      else if (d.emailed) message.success('寄信成功');
      else message.warning(`寄信失敗（已產生連結，請改用複製連結）：${d.emailError ?? '未知原因'}`);
    },
    onError: (e: Error) => message.error(e.message),
  });
  const submit = (send: boolean) => form.validateFields().then((v) => invite.mutate({ ...v, send }));
  const close = () => { setLink(null); onClose(); };

  return (
    <Drawer title="邀請使用者" open={open} onClose={close} width={drawerWidth}>
      <Form form={form} layout="vertical" className="mt-3">
        <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: '請輸入有效 email' }]}>
          <Input placeholder="someone@example.com" />
        </Form.Item>
        <Form.Item name="roleId" label="角色" rules={[{ required: true, message: '請選擇角色' }]}>
          <Select options={roles} placeholder="選擇角色" />
        </Form.Item>
        <Space>
          <Button type="primary" loading={invite.isPending} onClick={() => submit(true)}>
            寄送邀請信
          </Button>
          <Button loading={invite.isPending} onClick={() => submit(false)}>
            產生邀請連結
          </Button>
        </Space>
      </Form>
      {link && (
        <div className="mt-4">
          <Typography.Text type="secondary" className="text-xs">
            邀請連結（7 天內有效）：
          </Typography.Text>
          <Input.Search
            className="mt-1"
            value={link}
            enterButton={<CopyOutlined />}
            onSearch={() => {
              navigator.clipboard.writeText(link);
              message.success('已複製');
            }}
          />
        </div>
      )}
    </Drawer>
  );
}
